import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  verticalLifecycleApi,
  type CapabilityLayer,
  type CapabilityMap,
  type CustomerDetail,
  type VerticalCustomerRow,
  type VerticalLifecycleState,
  type VerticalSummary,
} from '@/api/verticalLifecycle';
import { PricingTab } from './PricingTab';
import { VerticalCostsTab } from './VerticalCostsTab';
import { OperatorsTab } from './OperatorsTab';

type ConsoleTab = 'customers' | 'pricing' | 'costs' | 'operators';

// Parameterized vertical customer admin console — one operator surface for
// Sensarra, Sakme, and future verticals (replaces the Sensarra-hardcoded
// WaitlistTab). Engine-tenant (global/blast-radius) actions live in the separate
// Director "Engine tenant" admin, NOT here — this console only performs
// vertical-scoped lifecycle actions, never a global tenant suspend.

const ACTIVE_STATES: VerticalLifecycleState[] = ['active_free', 'active_paid'];

function stateColor(state: VerticalLifecycleState): string {
  if (ACTIVE_STATES.includes(state)) return 'text-[#39FF14]';
  if (state === 'application_rejected' || state === 'past_due' || state === 'engine_blocked') return 'text-red-400';
  if (state === 'vertical_deactivated' || state === 'cancelled') return 'text-orange-400';
  return 'text-yellow-300';
}

function present(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '-' : date.toISOString().replace('T', ' ').slice(0, 16);
}

function errorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(error.message) as { detail?: unknown; message?: unknown };
    const detail = parsed.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const m = (detail as { message?: unknown }).message;
      if (typeof m === 'string') return m;
    }
    if (typeof parsed.message === 'string') return parsed.message;
  } catch {
    /* ApiError.message may be a plain body string */
  }
  return error.message;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-600">{label}</div>
      <div className="text-xs text-gray-300 whitespace-pre-wrap break-words">{value}</div>
    </div>
  );
}

function CapabilityLayerView({ layer }: { layer: CapabilityLayer }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] text-gray-300">{layer.description}</div>
      <ul className="space-y-1">
        {layer.items.map((item) => (
          <li key={item.key} className="text-[11px] text-gray-400">
            <span className="text-gray-200">{item.label}</span>{' '}
            <span className="text-gray-600">[{item.status}]</span>
            {item.note ? <div className="text-[10px] text-gray-600">{item.note}</div> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function VerticalAdminConsole({ vertical }: { vertical: string }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ConsoleTab>('customers');
  const [verticals, setVerticals] = useState<VerticalSummary[]>([]);
  // Super-operator (CTO master / Director) — the only role that may grant/revoke
  // founder operator seats. Derived from the admin-verticals probe's is_director.
  const [isSuperOperator, setIsSuperOperator] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<VerticalCustomerRow[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [capabilities, setCapabilities] = useState<CapabilityMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [approvalTenantId, setApprovalTenantId] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [deactivateReason, setDeactivateReason] = useState('');

  const rowKey = (row: VerticalCustomerRow) => row.tenant_id ?? `app:${row.application_id}`;

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [adminVerticals, customersResp] = await Promise.all([
        verticalLifecycleApi.listAdminVerticals(),
        verticalLifecycleApi.listCustomers(vertical),
      ]);
      setVerticals(adminVerticals.verticals);
      setIsSuperOperator(adminVerticals.is_director);
      setCustomers(customersResp.customers);
      setSelectedKey((current) =>
        current && customersResp.customers.some((c) => rowKey(c) === current)
          ? current
          : customersResp.customers[0]
            ? rowKey(customersResp.customers[0])
            : null,
      );
    } catch (error) {
      if (error instanceof Error && error.message.includes('403')) {
        setAccessError(`You are not authorized to administer the ${vertical} vertical.`);
      } else {
        setLoadError(errorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  }, [vertical]);

  useEffect(() => {
    setAccessError(null);
    setSelectedKey(null);
    void loadCustomers();
  }, [loadCustomers]);

  const selected = useMemo(
    () => customers.find((c) => rowKey(c) === selectedKey) ?? null,
    [customers, selectedKey],
  );

  // Launch mode of the vertical being administered. In `public` mode (the
  // platform / Seitiate+ vertical) public signups do NOT create early-access
  // application rows, so an empty Customers tab is STRUCTURAL, not data loss —
  // the empty-state copy branches on this (see below). Falls back to null until
  // the admin-verticals probe resolves.
  const launchMode = useMemo(
    () => verticals.find((v) => v.vertical_id === vertical)?.launch_mode ?? null,
    [verticals, vertical],
  );

  // vertical_id → display name, for the cross-reference "also appears as" hint.
  const verticalName = useCallback(
    (id: string) => verticals.find((v) => v.vertical_id === id)?.display_name ?? id,
    [verticals],
  );

  // Fix 3 — best-effort, read-only "same email also appears as ..." hint. The
  // backend correlates the customer's email (lowercased) across the two separate
  // customer tables (engine billing tenants + early-access applications) without
  // consolidating them or gating registration. We surface only the OTHER
  // appearances: applications in verticals other than the one being viewed, and
  // (for a tenant-less applicant) an engine account. Degrades to nothing when the
  // cross_reference is absent — older responses / mocks simply show no hint.
  const crossRefParts = useMemo<string[]>(() => {
    const xref = detail?.cross_reference;
    if (!xref) return [];
    const parts: string[] = [];
    const otherVerticals = (xref.application_verticals ?? []).filter((id) => id !== vertical);
    if (otherVerticals.length > 0) {
      const noun = otherVerticals.length > 1 ? 'applications' : 'application';
      parts.push(`${noun} in ${otherVerticals.map(verticalName).join(', ')}`);
    }
    if (xref.has_engine_account && !selected?.tenant_id) parts.push('an engine account');
    return parts;
  }, [detail, vertical, selected?.tenant_id, verticalName]);

  // Load detail + capabilities when a customer with a tenant is selected.
  useEffect(() => {
    setDetail(null);
    setCapabilities(null);
    setActionError(null);
    setActionMessage(null);
    if (!selected?.tenant_id) return;
    let cancelled = false;
    const tenantId = selected.tenant_id;
    Promise.all([
      verticalLifecycleApi.getCustomer(vertical, tenantId),
      verticalLifecycleApi.getCapabilities(vertical, tenantId),
    ])
      .then(([d, c]) => {
        if (cancelled) return;
        setDetail(d);
        setCapabilities(c);
      })
      .catch(() => {/* detail is best-effort; the row already shows core state */});
    return () => {
      cancelled = true;
    };
  }, [vertical, selected?.tenant_id]);

  const runAction = async (key: string, fn: () => Promise<string>) => {
    setActionLoading(key);
    setActionError(null);
    setActionMessage(null);
    try {
      const message = await fn();
      setActionMessage(message);
      await loadCustomers();
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const onApprove = (row: VerticalCustomerRow) =>
    runAction('approve', async () => {
      if (!row.application_id) throw new Error('No application to approve.');
      const res = await verticalLifecycleApi.approve(vertical, row.application_id, {
        tenant_id: approvalTenantId.trim() || null,
        notes: approvalNotes.trim() || null,
      });
      setApprovalTenantId('');
      setApprovalNotes('');
      return `Approved ${res.application.email}. Tenant ${res.tenant_id}${res.tenant_created ? ' (created)' : ''}.`;
    });

  const onReject = (row: VerticalCustomerRow) =>
    runAction('reject', async () => {
      if (!row.application_id) throw new Error('No application to reject.');
      if (!rejectReason.trim()) throw new Error('Reject requires a reason.');
      const res = await verticalLifecycleApi.reject(vertical, row.application_id, { reason: rejectReason.trim() });
      setRejectReason('');
      return `Rejected ${res.application.email}.`;
    });

  const onActivateFree = (row: VerticalCustomerRow) =>
    runAction('activate-free', async () => {
      await verticalLifecycleApi.activateFree(vertical, row.tenant_id!);
      return `Activated the free tier for ${row.tenant_id}.`;
    });

  const onDeactivate = (row: VerticalCustomerRow) =>
    runAction('deactivate', async () => {
      await verticalLifecycleApi.deactivate(vertical, row.tenant_id!, deactivateReason.trim() || undefined);
      setDeactivateReason('');
      return `Deactivated ${row.tenant_id} for ${vertical} (engine tenant untouched).`;
    });

  const onReactivate = (row: VerticalCustomerRow) =>
    runAction('reactivate', async () => {
      await verticalLifecycleApi.reactivate(vertical, row.tenant_id!);
      return `Reactivated ${row.tenant_id} for ${vertical}.`;
    });

  const onResend = (row: VerticalCustomerRow) =>
    runAction('resend', async () => {
      await verticalLifecycleApi.resendAccessEmail(vertical, row.tenant_id!);
      return `Access email re-sent for ${row.tenant_id}.`;
    });

  if (accessError) {
    return (
      <div className="p-6 font-mono text-sm text-gray-400 space-y-3">
        <div className="text-xs uppercase tracking-wider text-gray-600">Vertical Admin</div>
        <div className="border border-red-500/30 bg-red-500/5 text-red-300 rounded p-3">{accessError}</div>
      </div>
    );
  }

  const isPending = selected?.lifecycle_state === 'application_pending';
  const isActive = selected ? ACTIVE_STATES.includes(selected.lifecycle_state) : false;
  const isDeactivated = selected?.lifecycle_state === 'vertical_deactivated';

  return (
    <div className="font-mono text-sm text-gray-200 h-full flex flex-col">
      <div className="px-6 pt-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Vertical Management Center</div>
            <div className="text-[11px] text-gray-600 mt-1">
              One operator surface per vertical — customers, pricing, and compute/costs. Vertical-scoped actions only.
            </div>
          </div>
          <label className="text-xs text-gray-500">
            Vertical{' '}
            <select
              aria-label="Vertical"
              value={vertical}
              onChange={(e) => navigate(`/admin/verticals/${e.target.value}`)}
              className="ml-1 bg-black/40 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200"
            >
              {(verticals.length ? verticals : [{ vertical_id: vertical, display_name: vertical } as VerticalSummary]).map((vert) => (
                <option key={vert.vertical_id} value={vert.vertical_id}>
                  {vert.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <nav className="flex gap-1 border-b border-gray-800 -mb-px">
          {(
            [
              ['customers', 'Customers'],
              ['pricing', 'Pricing'],
              ['costs', 'Compute & Costs'],
              // Founder handover — super-operator only (grant/revoke operator seats).
              ...(isSuperOperator ? ([['operators', 'Operators']] as const) : []),
            ] as const
          ).map(
            ([key, lbl]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`px-3 py-2 text-xs border-b-2 ${tab === key ? 'border-[#39FF14] text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
              >
                {lbl}
              </button>
            ),
          )}
        </nav>
      </div>

      {tab === 'pricing' ? (
        <div className="flex-1 min-h-0 overflow-auto">
          <PricingTab vertical={vertical} />
        </div>
      ) : tab === 'costs' ? (
        <div className="flex-1 min-h-0 overflow-auto">
          <VerticalCostsTab vertical={vertical} />
        </div>
      ) : tab === 'operators' && isSuperOperator ? (
        <div className="flex-1 min-h-0 overflow-auto">
          <OperatorsTab vertical={vertical} />
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-4">
      {actionMessage && (
        <div role="status" className="mb-4 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs rounded p-3">
          {actionMessage}
        </div>
      )}
      {actionError && (
        <div role="alert" className="mb-4 border border-red-500/30 bg-red-500/5 text-red-300 text-xs rounded p-3">
          {actionError}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Loading {vertical} customers...</p>
      ) : loadError ? (
        <div className="text-red-400 text-xs">
          <p>Failed to load {vertical} customers.</p>
          <p className="text-gray-500 mt-1">{loadError}</p>
        </div>
      ) : customers.length === 0 ? (
        launchMode === 'public' ? (
          <p className="text-gray-500">
            No self-serve signups are tracked in this per-vertical view. Platform members sign up
            directly and appear in Command Center &rarr; Customers/Tenants.
          </p>
        ) : (
          <p className="text-gray-500">No {vertical} customers or applicants yet.</p>
        )
      ) : (
        <div className="grid grid-cols-[minmax(520px,1fr)_460px] gap-4 min-h-0 flex-1">
          <div className="overflow-auto border border-gray-800 rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-900/90 text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Customer</th>
                  <th className="text-left px-3 py-2 font-normal">State</th>
                  <th className="text-left px-3 py-2 font-normal">Tier</th>
                  <th className="text-left px-3 py-2 font-normal">Credits</th>
                  <th className="text-left px-3 py-2 font-normal">Admitted</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((row) => {
                  const key = rowKey(row);
                  return (
                    <tr
                      key={key}
                      className={`border-t border-gray-800 hover:bg-gray-900/40 cursor-pointer ${key === selectedKey ? 'bg-gray-900/60' : ''}`}
                      onClick={() => setSelectedKey(key)}
                    >
                      <td className="px-3 py-2">
                        <div className="text-gray-200">{present(row.email ?? row.tenant_id)}</div>
                        <div className="text-gray-500">{present(row.tenant_id)}</div>
                      </td>
                      <td className={`px-3 py-2 ${stateColor(row.lifecycle_state)}`}>{row.lifecycle_state}</td>
                      <td className="px-3 py-2 text-gray-400">{present(row.effective_tier)}</td>
                      <td className="px-3 py-2 text-gray-400">${row.credit_balance_usd}</td>
                      <td className={`px-3 py-2 ${row.admitted ? 'text-[#39FF14]' : 'text-gray-600'}`}>
                        {row.admitted ? 'yes' : 'no'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <aside className="border border-gray-800 rounded bg-black/20 p-4 overflow-auto">
            {!selected ? (
              <p className="text-gray-500 text-xs">Select a customer to inspect details.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-600">Selected</div>
                  <div className="text-lg text-gray-100 mt-1">{present(selected.email ?? selected.tenant_id)}</div>
                  <div className={`text-xs mt-1 ${stateColor(selected.lifecycle_state)}`}>{selected.lifecycle_state}</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <DetailRow label="Tenant ID" value={present(selected.tenant_id)} />
                  <DetailRow label="Engine status" value={present(selected.engine_tenant_status)} />
                  <DetailRow label="Subscription" value={present(selected.subscription_status)} />
                  <DetailRow label="Tier" value={present(selected.subscription_tier ?? selected.effective_tier)} />
                  <DetailRow label="Credit balance" value={`$${selected.credit_balance_usd}`} />
                  <DetailRow label="Application" value={present(selected.application_status)} />
                </div>

                {crossRefParts.length > 0 && (
                  <div className="border border-yellow-500/20 bg-yellow-500/5 text-yellow-300/90 text-[11px] rounded p-2">
                    <span className="text-gray-400">Same email also appears as:</span>{' '}
                    {crossRefParts.join('; ')}.
                    <div className="text-[10px] text-gray-600 mt-1">
                      Best-effort email match across the two customer tables — an operator hint, not a link.
                    </div>
                  </div>
                )}

                {detail?.last_error && (
                  <div className="border border-red-500/30 bg-red-500/5 text-red-300 text-[11px] rounded p-2">
                    Last lifecycle error: {present(JSON.stringify(detail.last_error.detail))}
                  </div>
                )}

                {detail && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Recent lifecycle events</div>
                    {detail.recent_events.length === 0 ? (
                      <div className="text-[11px] text-gray-600">None yet.</div>
                    ) : (
                      <ul className="space-y-1">
                        {detail.recent_events.slice(0, 6).map((ev, i) => (
                          <li key={i} className="text-[11px] text-gray-400">
                            <span className={ev.status === 'failed' ? 'text-red-400' : 'text-gray-300'}>{ev.event_type}</span>{' '}
                            <span className="text-gray-600">{formatDateTime(ev.created_at)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {capabilities && (
                  <div className="border-t border-gray-800 pt-3 space-y-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-600">Capabilities — what this customer has</div>
                    <CapabilityLayerView layer={capabilities.substrate} />
                    <CapabilityLayerView layer={capabilities.vertical_default} />
                    <CapabilityLayerView layer={capabilities.subscription_gated} />
                    {capabilities.notes.map((note, i) => (
                      <div key={i} className="text-[10px] text-gray-600">{note}</div>
                    ))}
                  </div>
                )}

                {/* Vertical-scoped lifecycle actions */}
                <div className="border-t border-gray-800 pt-4 space-y-4">
                  {isPending && (
                    <>
                      <div className="space-y-2">
                        <div className="text-[10px] uppercase tracking-wider text-gray-600">Approve (resolves the tenant by email)</div>
                        <label className="block text-[11px] text-gray-500">
                          Override tenant ID (optional — leave blank to resolve by email)
                          <input
                            value={approvalTenantId}
                            onChange={(e) => setApprovalTenantId(e.target.value)}
                            className="mt-1 w-full bg-black/40 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200"
                            placeholder="(resolve by email)"
                          />
                        </label>
                        <label className="block text-[11px] text-gray-500">
                          Notes (optional)
                          <textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            className="mt-1 w-full min-h-12 bg-black/40 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200"
                          />
                        </label>
                        <button
                          type="button"
                          disabled={actionLoading !== null}
                          onClick={() => void onApprove(selected)}
                          className="w-full px-3 py-2 text-left text-xs border border-[#39FF14]/30 rounded text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50"
                        >
                          {actionLoading === 'approve' ? 'Approving...' : 'Approve application'}
                        </button>
                      </div>
                      <div className="space-y-2">
                        <div className="text-[10px] uppercase tracking-wider text-gray-600">Reject</div>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="w-full min-h-12 bg-black/40 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200"
                          placeholder="Reason"
                        />
                        <button
                          type="button"
                          disabled={actionLoading !== null}
                          onClick={() => void onReject(selected)}
                          className="w-full px-3 py-2 text-left text-xs border border-red-500/30 rounded text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                        >
                          {actionLoading === 'reject' ? 'Rejecting...' : 'Reject application'}
                        </button>
                      </div>
                    </>
                  )}

                  {selected.tenant_id && (
                    <div className="space-y-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-600">Customer actions</div>
                      {!isActive && !isDeactivated && (
                        <button type="button" disabled={actionLoading !== null} onClick={() => void onActivateFree(selected)}
                          className="w-full px-3 py-2 text-left text-xs border border-[#39FF14]/30 rounded text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50">
                          {actionLoading === 'activate-free' ? 'Activating...' : 'Activate free tier'}
                        </button>
                      )}
                      {isDeactivated && (
                        <button type="button" disabled={actionLoading !== null} onClick={() => void onReactivate(selected)}
                          className="w-full px-3 py-2 text-left text-xs border border-[#39FF14]/30 rounded text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50">
                          {actionLoading === 'reactivate' ? 'Reactivating...' : 'Reactivate (vertical only)'}
                        </button>
                      )}
                      {isActive && (
                        <>
                          <input
                            value={deactivateReason}
                            onChange={(e) => setDeactivateReason(e.target.value)}
                            className="w-full bg-black/40 border border-gray-800 rounded px-2 py-1 text-xs text-gray-200"
                            placeholder="Deactivation reason (optional)"
                          />
                          <button type="button" disabled={actionLoading !== null} onClick={() => void onDeactivate(selected)}
                            className="w-full px-3 py-2 text-left text-xs border border-orange-500/30 rounded text-orange-300 hover:bg-orange-500/10 disabled:opacity-50">
                            {actionLoading === 'deactivate' ? 'Deactivating...' : 'Deactivate (this vertical only)'}
                          </button>
                        </>
                      )}
                      <button type="button" disabled={actionLoading !== null} onClick={() => void onResend(selected)}
                        className="w-full px-3 py-2 text-left text-xs border border-gray-700 rounded text-gray-300 hover:bg-gray-800/40 disabled:opacity-50">
                        {actionLoading === 'resend' ? 'Sending...' : 'Resend access email'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
        </div>
      )}
    </div>
  );
}
